import { iButtonProps } from '@/types/app.types'
import React from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/utils'
import { BUTTON_STYLE, BUTTON_TYPE } from '@/app.constants'

const Button = React.forwardRef<HTMLButtonElement, iButtonProps>((props, ref) => {
  const {
    ofType = BUTTON_TYPE.REGULAR,
    ofStyle = BUTTON_STYLE.FILLED,
    alignIcon = 'left',
    iconSize = 20,
    loading = false,
    disabled = false,
    icon,
    iconColor,
    children,
    dataTestId,
    className,
    contentClassName,
    ...rest
  } = props

  const Icon = icon

  // check if the className contains the class name of the variant (danger or success)
  const isDangerVariant = typeof className === 'string' && className.includes('omniful-danger-btn')
  const isSuccessVariant = typeof className === 'string' && className.includes('omniful-success-btn')

  // define the type classes for the button
  const typeClasses = {
    [BUTTON_TYPE.REGULAR]: 'gap-3 w-fit px-4 py-[9px] rounded-[8px] text-[14px] leading-[20px]',
    [BUTTON_TYPE.COMPACT]: 'gap-[10px] w-fit px-3 py-[7px] rounded-[6px] text-[14px] leading-[20px]',
    [BUTTON_TYPE.COMPRESSED]: 'gap-2 w-fit px-2 py-[3px] rounded-[4px] text-[11px] leading-[16px]',
  } as const

  // define the style classes for the button
  const styleClasses = {
    [BUTTON_STYLE.FILLED]:
      'bg-primary text-white border border-primary hover:border-primary_dark hover:bg-primary_dark hover:[box-shadow:2px_2px_4px_0px_#CCD2FD] active:border-primary_dark active:bg-primary_dark disabled:border-primary_light disabled:bg-primary_light disabled:[box-shadow:none]',
    [BUTTON_STYLE.OUTLINED]:
      'bg-white text-primary border border-primary hover:text-primary_dark hover:border-primary_dark hover:[box-shadow:2px_2px_4px_0px_#CCD2FD] active:border-primary_dark active:text-primary_dark disabled:border-primary_light disabled:text-primary_light disabled:[box-shadow:none]',
    [BUTTON_STYLE.NO_BACKGROUND]:
      'bg-transparent text-primary border-none hover:text-primary_dark hover:bg-primary_lighter active:text-primary_dark active:bg-transparent disabled:text-primary_light disabled:bg-transparent disabled:[box-shadow:none]',
    [BUTTON_STYLE.TEXT_ONLY]:
      'bg-transparent text-primary border-none hover:text-primary_dark hover:bg-transparent active:text-primary_dark active:bg-transparent disabled:text-primary_light disabled:bg-transparent disabled:[box-shadow:none]',
  } as const

  // define the custom variant classes for the button
  const customVariantClasses = cn({
    'bg-white text-danger border border-danger hover:text-danger hover:border-danger hover:[box-shadow:2px_2px_4px_0px_#FFE5E5] active:border-danger active:text-danger disabled:border-danger_light disabled:text-danger_light disabled:[box-shadow:none]':
      isDangerVariant,
    'bg-white text-success border border-success hover:text-success hover:border-success hover:[box-shadow:2px_2px_4px_0px_#E6F1E6] active:border-success active:text-success disabled:border-success_light disabled:text-success_light disabled:[box-shadow:none]':
      isSuccessVariant,
  })

  return (
    <button
      data-testid={dataTestId}
      className={cn(
        'flex h-fit box-border items-center justify-between text-[14px] leading-[20px] font-normal transition-shadow duration-300 ease-linear disabled:cursor-not-allowed disabled:hover:shadow-none focus-visible:[outline:2px_solid_var(--warning)] focus-visible:[animation:focus-glow-pulse_1.5s_linear_infinite]',
        typeClasses[ofType],
        styleClasses[ofStyle],
        customVariantClasses,
        className,
      )}
      disabled={loading || disabled}
      {...rest}
      ref={ref}
    >
      <span className={cn('flex items-center justify-center gap-[12px] !border-none', contentClassName)}>
        {loading && alignIcon === 'left' && <Loader2 size={16} className='animate-spin shrink-0' />}
        {!loading && Icon && alignIcon === 'left' && <Icon size={iconSize} color={iconColor} className='shrink-0' />}
        {children}
        {!loading && Icon && alignIcon === 'right' && <Icon size={iconSize} color={iconColor} className='shrink-0' />}
        {loading && alignIcon === 'right' && <Loader2 size={16} className='animate-spin shrink-0' />}
      </span>
    </button>
  )
})

Button.displayName = 'Button'

export default Button
